import { Link } from "react-router-dom";
import {
  Landmark,
  ShieldCheck,
  Megaphone,
  Code2,
  CreditCard,
  Ruler,
  Clapperboard,
  PartyPopper,
  Gem,
  ArrowRight,
  MessageCircle,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/lib/api";

const GROUPS = [
  {
    label: "Finance",
    items: [
      {
        icon: Landmark,
        title: "Accelerate Your Funding & Liquidity",
        desc: "We structure and secure the exact capital you need — from fast-tracked unsecured business loans and multi-crore project financing to the best rates on retail home and car loans.",
        to: "/loans",
      },
      {
        icon: ShieldCheck,
        title: "Protect & Multiply Your Wealth",
        desc: "Personalized wealth portfolios (Mutual Funds, SIPs, Digital Gold) and comprehensive insurance (Term Life, Mediclaim, Keyman) that safeguard your family and business while growing your assets.",
        to: "/wealth",
      },
      {
        icon: CreditCard,
        title: "Optimize Your Cash Flow",
        desc: "Instant financial flexibility through secure credit card bill swiping — access liquidity, automate large rent or vendor payments, and bypass high-interest penalties while earning rewards.",
        to: "/credit-card-liquidity",
      },
    ],
  },
  {
    label: "Digital",
    items: [
      {
        icon: Megaphone,
        title: "Scale Your Brand Digitally",
        desc: "Highly targeted Meta and Google campaigns to capture high-intent leads, backed by robust SEO and LinkedIn B2B strategies that let you dominate your market.",
        to: "/digital-marketing",
      },
      {
        icon: Code2,
        title: "Deploy Robust IT Infrastructure",
        desc: "Custom conversion-focused websites, secure e-commerce platforms, and CRM plus WhatsApp automation so your sales pipeline is completely streamlined.",
        to: "/it-services",
      },
      {
        icon: Clapperboard,
        title: "Capture Your Story on Camera",
        desc: "Cinematic weddings and pre-wedding shoots, multi-camera concert coverage, corporate brand films and architectural drone cinematography — visual narratives that leave a lasting legacy.",
        to: "/videography-photography",
      },
    ],
  },
  {
    label: "Spaces & Experiences",
    items: [
      {
        icon: Ruler,
        title: "Design Visionary Spaces",
        desc: "End-to-end turnkey architectural and interior projects for residential homes and corporate workspaces — a physical footprint that matches your brand's ambition.",
        to: "/interior-design",
      },
      {
        icon: PartyPopper,
        title: "Execute Flawless Events",
        desc: "Weddings, live concerts, corporate conferences, product launches and milestone celebrations — planned to the minute and delivered under one unified Evolve Elite manager.",
        to: "/event-management",
      },
      {
        icon: Gem,
        title: "Curate Unforgettable Weddings",
        desc: "Breathtaking destination weddings at heritage palaces, beachfronts and luxury resorts — venue, guest hospitality, decor, artists and an on-ground shadow team, all handled.",
        to: "/destination-weddings",
      },
    ],
  },
];

export const Deliverables = () => (
  <section className="border-t border-slate-200 py-16 sm:py-24 lg:py-32" data-testid="deliverables-section">
    <div className="container-x">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-16">
        <Reveal className="lg:col-span-5">
          <p className="eyebrow">Your Unified Growth Partner</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
            Fund it. Grow it. Build it. Celebrate it.{" "}
            <span className="text-amber-600">One partner</span> for everything your growth needs.
          </h2>
        </Reveal>
        <Reveal delay={0.12} className="lg:col-span-7 lg:pt-1.5">
          <p className="text-base leading-relaxed text-slate-600 sm:text-lg">
            We eliminate the friction of scaling by acting as your single, unified growth partner. Instead of
            coordinating with multiple fragmented agencies, you gain access to a cohesive team that actively drives your
            financial, digital, physical and experiential expansion.
          </p>
          <p className="mt-4 text-sm font-bold text-navy">Here is exactly what we deliver to our clients:</p>
        </Reveal>
      </div>

      <div className="mt-14 space-y-12">
        {GROUPS.map((g, gi) => (
          <div key={g.label} data-testid={`deliverable-group-${gi + 1}`}>
            <Reveal>
              <div className="flex items-center gap-4">
                <span className="font-mono text-xs font-semibold text-slate-400">0{gi + 1}</span>
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-600">{g.label}</p>
                <span className="h-px flex-1 bg-slate-200" />
              </div>
            </Reveal>
            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
              {g.items.map((d, i) => (
                <Reveal key={d.title} delay={i * 0.07}>
                  <Link
                    to={d.to}
                    className="card-lift group flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-7"
                    data-testid={`deliverable-${gi * 3 + i + 1}`}
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-navy">
                      <d.icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-6 font-display text-xl font-extrabold text-navy">{d.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{d.desc}</p>
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-bold text-amber-600 transition-all group-hover:gap-2.5">
                      Explore <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div
          className="grain mt-14 flex flex-col items-start gap-6 rounded-3xl bg-navy p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10"
          data-testid="deliverables-cta"
        >
          <p className="font-display text-2xl font-extrabold text-white sm:text-3xl">Ready to streamline your growth?</p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link to="/contact" className="btn-gold" data-testid="about-consultation-cta">
              Book a Comprehensive Consultation <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp px-6 py-3 text-sm font-bold text-white transition hover:brightness-110"
              data-testid="about-whatsapp-cta"
            >
              <MessageCircle className="h-4 w-4" /> Chat with an Expert via WhatsApp
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
